import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getCardsByPack, getPackById } from "../db";
import { deleteCard, putCard } from "./adminApi";
import type { Card, Pack } from "../domain/types";
import { CARD_TYPES, type CardType, type Phase } from "../domain/enums";
import { Button } from "../components/Button";
import { CARD_TYPE_ICON, CARD_TYPE_TINT } from "../components/modeTheme";

interface Props {
  mode: "new" | "edit";
}

type InteractionMode =
  | "self"
  | "ask"
  | "shout"
  | "vote_room"
  | "compete"
  | "chain"
  | "mirror"
  | "solo";

interface FormState {
  baseId: string;
  type: CardType;
  phase: Phase;
  durationSec: number | "";
  participantsCount: number | "all" | "auto";
  kind: "main" | "reward" | "penalty";
  triggersConsequence: boolean;
  interactionMode: InteractionMode | "";
  answerTimeSec: number | "";
  reactionWindowSec: number | "";
  text_uk: string;
  text_ru: string;
  text_en: string;
}

interface BoundState {
  enabled: boolean;
  existed: boolean;
  /** "Зовсім без X для цієї картки" — engine never fires this side. Mutex
   *  with `enabled`. Persisted on the parent main card as suppressReward /
   *  suppressPenalty. */
  suppress: boolean;
  participantsCount: number | "all" | "auto";
  durationSec: number | "";
  text_uk: string;
  text_ru: string;
  text_en: string;
}

const PHASES: Phase[] = ["warmup", "engagement", "peak"];

const TYPE_LABEL_UK: Record<CardType, string> = {
  question: "Питання — словесна відповідь",
  task: "Завдання — фізична дія, покажи / зроби",
  mini_challenge: "Міні-челендж — на час, з таймером",
  vote: "Голосування — група голосує",
  pair: "Пара — із одним помічником",
  group: "Група — усі разом",
};

const PHASE_LABEL_UK: Record<Phase, string> = {
  warmup: "Розігрів — легкий початок",
  engagement: "Залучення — основна частина",
  peak: "Пік — найжорсткіше",
};

function defaultForm(): FormState {
  return {
    baseId: Math.random().toString(36).slice(2, 10),
    type: "question",
    phase: "warmup",
    durationSec: "",
    participantsCount: "auto",
    kind: "main",
    triggersConsequence: false,
    interactionMode: "",
    answerTimeSec: "",
    reactionWindowSec: "",
    text_uk: "",
    text_ru: "",
    text_en: "",
  };
}

function emptyBound(): BoundState {
  return {
    enabled: false,
    existed: false,
    suppress: false,
    participantsCount: "auto",
    durationSec: "",
    text_uk: "",
    text_ru: "",
    text_en: "",
  };
}

export default function AdminCardForm({ mode }: Props) {
  const { packId, cardId } = useParams<{ packId: string; cardId?: string }>();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [pack, setPack] = useState<Pack | null>(null);
  const presetKind = (() => {
    const k = searchParams.get("kind");
    return k === "reward" || k === "penalty" || k === "main" ? k : null;
  })();
  const [form, setForm] = useState<FormState>(() => {
    const f = defaultForm();
    if (presetKind) f.kind = presetKind;
    return f;
  });
  const [boundReward, setBoundReward] = useState<BoundState>(emptyBound);
  const [boundPenalty, setBoundPenalty] = useState<BoundState>(emptyBound);

  useEffect(() => {
    if (!packId) return;
    (async () => {
      setPack((await getPackById(packId)) ?? null);
      if (mode === "edit" && cardId) {
        const all = await getCardsByPack(packId);
        const triple = all.filter(
          (c) =>
            c.id === `uk:${cardId}` ||
            c.id === `ru:${cardId}` ||
            c.id === `en:${cardId}`,
        );
        const uk = triple.find((c) => c.language === "uk");
        const ru = triple.find((c) => c.language === "ru");
        const en = triple.find((c) => c.language === "en");
        const sample = uk ?? ru ?? en;
        if (sample) {
          const pc = sample.participantsCount;
          setForm({
            baseId: cardId,
            type: sample.type,
            phase: sample.phase,
            durationSec: sample.durationSec ?? "",
            participantsCount:
              pc == null
                ? "auto"
                : pc === "all"
                  ? "all"
                  : Math.max(0, Math.min(20, Number(pc))),
            kind: (sample.kind as FormState["kind"]) ?? "main",
            triggersConsequence: !!sample.triggersConsequence,
            interactionMode:
              (sample.interactionMode as InteractionMode | null | undefined) ??
              "",
            answerTimeSec:
              typeof sample.answerTimeSec === "number"
                ? sample.answerTimeSec
                : "",
            reactionWindowSec:
              typeof sample.reactionWindowSec === "number"
                ? sample.reactionWindowSec
                : "",
            text_uk: uk?.text ?? "",
            text_ru: ru?.text ?? "",
            text_en: en?.text ?? "",
          });
        }
        if ((sample?.kind ?? "main") === "main") {
          const childrenReward = all.filter(
            (c) =>
              (c.kind ?? "main") === "reward" && c.parentCardId === cardId,
          );
          const childrenPenalty = all.filter(
            (c) =>
              (c.kind ?? "main") === "penalty" && c.parentCardId === cardId,
          );
          setBoundReward(
            loadBound(childrenReward, !!sample?.suppressReward),
          );
          setBoundPenalty(
            loadBound(childrenPenalty, !!sample?.suppressPenalty),
          );
        }
      }
    })();
  }, [packId, cardId, mode]);

  function loadBound(children: Card[], suppress: boolean): BoundState {
    if (children.length === 0) {
      return { ...emptyBound(), suppress };
    }
    const uk = children.find((c) => c.language === "uk");
    const ru = children.find((c) => c.language === "ru");
    const en = children.find((c) => c.language === "en");
    const sample = uk ?? ru ?? en!;
    const pc = sample.participantsCount;
    return {
      enabled: true,
      existed: true,
      suppress,
      participantsCount:
        pc == null
          ? "auto"
          : pc === "all"
            ? "all"
            : Math.max(0, Math.min(20, Number(pc))),
      durationSec: sample.durationSec ?? "",
      text_uk: uk?.text ?? "",
      text_ru: ru?.text ?? "",
      text_en: en?.text ?? "",
    };
  }

  async function save() {
    if (!packId || !pack) return;
    if (!form.text_uk.trim()) {
      alert("UK text required");
      return;
    }
    const intensity = (form.phase === "warmup"
      ? 2
      : form.phase === "engagement"
        ? 3
        : 4) as 2 | 3 | 4;
    const energy = Math.min(
      5,
      intensity +
        (form.type === "task" || form.type === "mini_challenge" ? 1 : 0),
    ) as 1 | 2 | 3 | 4 | 5;
    const dur =
      form.type === "mini_challenge"
        ? Math.max(
            5,
            Math.min(
              300,
              form.durationSec === ""
                ? 30
                : Number(form.durationSec) || 30,
            ),
          )
        : null;
    const involvesOthers =
      form.type === "vote" ||
      form.type === "pair" ||
      form.type === "group";

    const pc: number | "all" | null =
      form.participantsCount === "auto"
        ? null
        : form.participantsCount === "all"
          ? "all"
          : Math.max(
              0,
              Math.min(20, Number(form.participantsCount) || 0),
            );

    const interactionMode =
      form.interactionMode === "" ? null : form.interactionMode;
    const answerTimeSec =
      form.answerTimeSec === ""
        ? null
        : Math.max(5, Math.min(180, Number(form.answerTimeSec) || 0));
    const reactionWindowSec =
      form.reactionWindowSec === ""
        ? null
        : Math.max(2, Math.min(15, Number(form.reactionWindowSec) || 0));

    const suppressReward = form.kind === "main" && boundReward.suppress;
    const suppressPenalty = form.kind === "main" && boundPenalty.suppress;
    const hasBoundReward =
      form.kind === "main" &&
      boundReward.enabled &&
      !boundReward.suppress &&
      !!boundReward.text_uk.trim();
    const hasBoundPenalty =
      form.kind === "main" &&
      boundPenalty.enabled &&
      !boundPenalty.suppress &&
      !!boundPenalty.text_uk.trim();
    const triggersConsequence =
      form.kind === "main"
        ? form.triggersConsequence || hasBoundReward || hasBoundPenalty
        : false;

    const make = (lang: "uk" | "ru" | "en", text: string): Card => ({
      id: `${lang}:${form.baseId}`,
      packId,
      modeId: pack.modeId,
      language: lang,
      type: form.type,
      phase: form.phase,
      text,
      intensity: intensity as Card["intensity"],
      energy,
      socialBoldness: intensity as Card["socialBoldness"],
      durationSec: dur,
      hasTimer: form.type === "mini_challenge",
      involvesOthers:
        involvesOthers ||
        (typeof pc === "number" && pc > 0) ||
        pc === "all",
      ageGate: pack.isAdult,
      participantsCount: pc,
      interactionMode,
      answerTimeSec,
      reactionWindowSec,
      kind: form.kind,
      triggersConsequence,
      suppressReward: form.kind === "main" ? suppressReward : null,
      suppressPenalty: form.kind === "main" ? suppressPenalty : null,
    });

    await putCard(make("uk", form.text_uk.trim()));
    if (form.text_ru.trim()) await putCard(make("ru", form.text_ru.trim()));
    if (form.text_en.trim()) await putCard(make("en", form.text_en.trim()));

    if (form.kind === "main") {
      await persistBound("reward", boundReward);
      await persistBound("penalty", boundPenalty);
    }

    const back = searchParams.get("back");
    const suffix = back && back !== "all" ? `?filter=${back}` : "";
    nav(`/admin/pack/${packId}${suffix}`);
  }

  async function persistBound(kind: "reward" | "penalty", b: BoundState) {
    if (!packId || !pack) return;
    const childBase = `${form.baseId}_bound_${kind}`;
    // Suppress wins: never save bound rows for a suppressed side. If they
    // existed before (host toggled "Зовсім без X" after writing bound text)
    // — wipe them so the engine doesn't trip over orphaned children.
    const enabledWithText =
      !b.suppress && b.enabled && b.text_uk.trim().length > 0;

    if (!enabledWithText) {
      if (b.existed) {
        await deleteCard(`uk:${childBase}`);
        await deleteCard(`ru:${childBase}`);
        await deleteCard(`en:${childBase}`);
      }
      return;
    }

    const intensity = (form.phase === "warmup"
      ? 2
      : form.phase === "engagement"
        ? 3
        : 4) as 2 | 3 | 4;
    const energy = Math.min(5, intensity + 1) as 1 | 2 | 3 | 4 | 5;
    const childPc: number | "all" | null =
      b.participantsCount === "auto"
        ? null
        : b.participantsCount === "all"
          ? "all"
          : Math.max(0, Math.min(20, Number(b.participantsCount) || 0));
    const dur =
      b.durationSec === ""
        ? null
        : Math.max(5, Math.min(300, Number(b.durationSec) || 0));
    const childType: CardType = dur != null ? "mini_challenge" : "task";

    const makeChild = (
      lang: "uk" | "ru" | "en",
      text: string,
    ): Card => ({
      id: `${lang}:${childBase}`,
      packId,
      modeId: pack.modeId,
      language: lang,
      type: childType,
      phase: form.phase,
      text,
      intensity: intensity as Card["intensity"],
      energy,
      socialBoldness: intensity as Card["socialBoldness"],
      durationSec: dur,
      hasTimer: dur != null,
      involvesOthers:
        (typeof childPc === "number" && childPc > 0) || childPc === "all",
      ageGate: pack.isAdult,
      participantsCount: childPc,
      interactionMode: null,
      answerTimeSec: null,
      reactionWindowSec: null,
      kind,
      triggersConsequence: false,
      parentCardId: form.baseId,
    });

    await putCard(makeChild("uk", b.text_uk.trim()));
    if (b.text_ru.trim()) await putCard(makeChild("ru", b.text_ru.trim()));
    else if (b.existed) await deleteCard(`ru:${childBase}`);
    if (b.text_en.trim()) await putCard(makeChild("en", b.text_en.trim()));
    else if (b.existed) await deleteCard(`en:${childBase}`);
  }

  return (
    <div className="flex flex-col gap-3 pb-6">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => nav(-1)}>
          ←
        </Button>
        <h3 className="font-display text-lg font-bold flex-1">
          {mode === "new" ? "Нова картка" : "Редагування картки"}
        </h3>
      </div>

      {pack && (
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-xs flex items-center gap-2">
          <span className="opacity-50">У колоду:</span>
          <span className="font-bold">{pack.titleByLang.uk}</span>
          <span className="opacity-50">·</span>
          <span className="opacity-70 uppercase tracking-wide">{pack.modeId}</span>
          {pack.isAdult && (
            <span className="ml-auto text-[10px] rounded-full bg-black/30 border border-white/30 px-1.5 py-0.5">
              18+
            </span>
          )}
        </div>
      )}

      {(() => {
        const back = searchParams.get("back");
        if (!back || back === "all") return null;
        const labels: Record<string, string> = {
          main: "← Список карток",
          reward: "← Список 🏆 нагород",
          penalty: "← Список 💀 покарань",
          trigger: "← Список ⚡ тригерів",
        };
        const label = labels[back] ?? "← Назад до списку";
        return (
          <button
            type="button"
            className="text-xs text-left opacity-70 hover:opacity-100 transition px-1"
            onClick={() => nav(`/admin/pack/${packId}?filter=${back}`)}
          >
            {label}
          </button>
        );
      })()}

      <Field label="Kind (вид картки)">
        <select
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.kind}
          onChange={(e) =>
            setForm({ ...form, kind: e.target.value as FormState["kind"] })
          }
        >
          <option value="main">main — основна (грається з черги)</option>
          <option value="reward">🏆 reward — нагорода (DONE-наслідок)</option>
          <option value="penalty">💀 penalty — покарання (FAILED-наслідок)</option>
        </select>
      </Field>

      <Field label="Тип картки">
        <select
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value as CardType })}
        >
          {CARD_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL_UK[t]}
            </option>
          ))}
        </select>
      </Field>

      {form.kind === "main" && (
        <Field label="Тригерить наслідок (нагорода/покарання)">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.triggersConsequence}
              onChange={(e) =>
                setForm({ ...form, triggersConsequence: e.target.checked })
              }
              className="w-5 h-5"
            />
            <span className="text-sm opacity-80">
              На Виконано — 🏆 (власна якщо є, інакше з пулу). На Не вийшло — 💀.
            </span>
          </label>
        </Field>
      )}

      <Field label="Фаза гри (коли вона з'являється)">
        <select
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.phase}
          onChange={(e) => setForm({ ...form, phase: e.target.value as Phase })}
        >
          {PHASES.map((p) => (
            <option key={p} value={p}>
              {PHASE_LABEL_UK[p]}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Скільки помічників (крім головного гравця)">
        <select
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={String(form.participantsCount)}
          onChange={(e) => {
            const v = e.target.value;
            setForm({
              ...form,
              participantsCount:
                v === "auto" || v === "all"
                  ? (v as "auto" | "all")
                  : Number(v),
            });
          }}
        >
          <option value="auto">auto (за типом картки)</option>
          <option value="0">0 — solo</option>
          <option value="1">1 помічник</option>
          <option value="2">2 помічники</option>
          <option value="3">3 помічники</option>
          <option value="4">4 помічники</option>
          <option value="5">5 помічників</option>
          <option value="all">all — вся група</option>
        </select>
      </Field>

      <Field label="Як грається (підказка для хоста)">
        <select
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.interactionMode}
          onChange={(e) =>
            setForm({
              ...form,
              interactionMode: e.target.value as FormState["interactionMode"],
            })
          }
        >
          <option value="">— auto / none —</option>
          <option value="self">self — гравець про себе</option>
          <option value="ask">ask — обирає помічника, той відповідає</option>
          <option value="shout">shout — хто перший вигукне</option>
          <option value="vote_room">vote_room — група голосує</option>
          <option value="compete">compete — змагання, група судить</option>
          <option value="chain">chain — по черзі по одному</option>
          <option value="mirror">mirror — пара дзеркалить</option>
          <option value="solo">solo — явне соло</option>
        </select>
      </Field>

      <Field label="Таймер відповіді (сек, 5–180; пусто = без таймера)">
        <input
          type="number"
          min={5}
          max={180}
          step={5}
          placeholder="(без таймера)"
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.answerTimeSec}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return setForm({ ...form, answerTimeSec: "" });
            const n = Number(v);
            setForm({
              ...form,
              answerTimeSec: Number.isFinite(n)
                ? Math.max(5, Math.min(180, n))
                : "",
            });
          }}
        />
      </Field>

      <Field label="«РЕАГУЙТЕ!» після картки (сек, 2–15; пусто = без етапу)">
        <input
          type="number"
          min={2}
          max={15}
          step={1}
          placeholder="(без РЕАГУЙТЕ-стейджа)"
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.reactionWindowSec}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return setForm({ ...form, reactionWindowSec: "" });
            const n = Number(v);
            setForm({
              ...form,
              reactionWindowSec: Number.isFinite(n)
                ? Math.max(2, Math.min(15, n))
                : "",
            });
          }}
        />
      </Field>

      {form.type === "mini_challenge" && (
        <Field label="Тривалість челенджу (сек, 5–300)">
          <input
            type="number"
            min={5}
            max={300}
            step={5}
            placeholder="30"
            className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
            value={form.durationSec}
            onChange={(e) => {
              if (e.target.value === "")
                return setForm({ ...form, durationSec: "" });
              const n = Number(e.target.value);
              const clamped = Math.max(
                5,
                Math.min(300, isFinite(n) ? n : 30),
              );
              setForm({ ...form, durationSec: clamped });
            }}
          />
        </Field>
      )}

      <Field label="Текст картки українською (обов'язково)">
        <textarea
          rows={2}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.text_uk}
          onChange={(e) => setForm({ ...form, text_uk: e.target.value })}
        />
      </Field>
      <ReadAloudMeter text={form.text_uk} />
      <CardPreview text={form.text_uk} type={form.type} />
      <Field label="Текст російською (необов'язково)">
        <textarea
          rows={2}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.text_ru}
          onChange={(e) => setForm({ ...form, text_ru: e.target.value })}
        />
      </Field>
      <Field label="Текст англійською (необов'язково)">
        <textarea
          rows={2}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.text_en}
          onChange={(e) => setForm({ ...form, text_en: e.target.value })}
        />
      </Field>

      {form.kind === "main" && (
        <>
          <BoundEditor
            kind="reward"
            state={boundReward}
            setState={setBoundReward}
          />
          <BoundEditor
            kind="penalty"
            state={boundPenalty}
            setState={setBoundPenalty}
          />
        </>
      )}

      <Button variant="primary" fullWidth onClick={save}>
        Save
      </Button>
    </div>
  );
}

function BoundEditor({
  kind,
  state,
  setState,
}: {
  kind: "reward" | "penalty";
  state: BoundState;
  setState: (s: BoundState) => void;
}) {
  const isReward = kind === "reward";
  const title = isReward
    ? "🏆 Власна нагорода для цієї картки"
    : "💀 Власне покарання для цієї картки";
  const accent = isReward
    ? "border-amber-400/40 bg-amber-400/8"
    : "border-red-400/40 bg-red-500/8";
  const subtitle = isReward
    ? "Подаватиметься на «Виконано» замість випадкової зі спільного пулу."
    : "Подаватиметься на «Не вийшло» замість випадкового зі спільного пулу.";
  const suppressLabel = isReward
    ? "⊘ Зовсім без нагороди для цієї картки"
    : "⊘ Зовсім без покарання для цієї картки";
  const suppressHint = isReward
    ? "На «Виконано» нічого не подавати — ні з пулу, ні власне."
    : "На «Не вийшло» нічого не подавати — ні з пулу, ні власне.";

  return (
    <div className={`rounded-xl border ${accent} p-3 flex flex-col gap-2`}>
      {/* Suppress wins — turning it on auto-disables the bound editor and on
          Save deletes any persisted bound rows. */}
      <label className="flex items-start justify-between gap-2 cursor-pointer">
        <div className="flex flex-col">
          <span className="text-sm font-bold">{suppressLabel}</span>
          <span className="text-[11px] opacity-70">{suppressHint}</span>
        </div>
        <input
          type="checkbox"
          checked={state.suppress}
          onChange={(e) =>
            setState({
              ...state,
              suppress: e.target.checked,
              enabled: e.target.checked ? false : state.enabled,
            })
          }
          className="w-5 h-5 mt-0.5"
        />
      </label>

      <label
        className={`flex items-center justify-between gap-2 ${
          state.suppress ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <div className="flex flex-col">
          <span className="text-sm font-bold">{title}</span>
          <span className="text-[11px] opacity-70">{subtitle}</span>
        </div>
        <input
          type="checkbox"
          checked={state.enabled}
          disabled={state.suppress}
          onChange={(e) => setState({ ...state, enabled: e.target.checked })}
          className="w-5 h-5"
        />
      </label>

      {state.enabled && !state.suppress && (
        <>
          <Field label="Скільки помічників (крім гравця в спот-лайті)">
            <select
              className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
              value={String(state.participantsCount)}
              onChange={(e) => {
                const v = e.target.value;
                setState({
                  ...state,
                  participantsCount:
                    v === "auto" || v === "all"
                      ? (v as "auto" | "all")
                      : Number(v),
                });
              }}
            >
              <option value="auto">auto — без помічників</option>
              <option value="0">0 — solo</option>
              <option value="1">1 помічник</option>
              <option value="2">2 помічники</option>
              <option value="3">3 помічники</option>
              <option value="all">all — уся група</option>
            </select>
          </Field>

          <Field label="Таймер на час дії (сек, 5–300; пусто = без таймера)">
            <input
              type="number"
              min={5}
              max={300}
              step={5}
              placeholder="(без таймера)"
              className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
              value={state.durationSec}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "")
                  return setState({ ...state, durationSec: "" });
                const n = Number(v);
                setState({
                  ...state,
                  durationSec: Number.isFinite(n)
                    ? Math.max(5, Math.min(300, n))
                    : "",
                });
              }}
            />
          </Field>

          <Field label="Текст українською (обов'язково для збереження)">
            <textarea
              rows={2}
              className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
              value={state.text_uk}
              onChange={(e) => setState({ ...state, text_uk: e.target.value })}
            />
          </Field>
          <Field label="Текст російською (необов'язково)">
            <textarea
              rows={2}
              className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
              value={state.text_ru}
              onChange={(e) => setState({ ...state, text_ru: e.target.value })}
            />
          </Field>
          <Field label="Текст англійською (необов'язково)">
            <textarea
              rows={2}
              className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
              value={state.text_en}
              onChange={(e) => setState({ ...state, text_en: e.target.value })}
            />
          </Field>
        </>
      )}
    </div>
  );
}

/**
 * Read-aloud length check — the host reads every card OUT LOUD to the room,
 * so length ≈ speaking time. Thresholds mirror the in-game font ladder:
 * ≤80 chars reads in one glance, >140 forces the smallest font and drags.
 */
function ReadAloudMeter({ text }: { text: string }) {
  const len = text.trim().length;
  if (len === 0) return null;
  const level =
    len <= 80
      ? { color: "#34d399", note: "читається одним поглядом" }
      : len <= 140
        ? { color: "#fbbf24", note: "довгувато — перевір уголос" }
        : { color: "#fb7185", note: "занадто довге для читання вголос — скороти" };
  return (
    <div className="flex items-center gap-2 text-[11px] -mt-1 px-1">
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: level.color }}
        aria-hidden
      />
      <span className="tabular-nums opacity-70">{len} симв.</span>
      <span style={{ color: level.color }}>{level.note}</span>
    </div>
  );
}

/** Live preview — the exact hero-card treatment the host will see in game. */
function CardPreview({ text, type }: { text: string; type: CardType }) {
  const trimmed = text.trim();
  if (!trimmed) return null;
  // Same font ladder as the Session card screen.
  const sizeClass =
    trimmed.length > 140
      ? "text-lg"
      : trimmed.length > 80
        ? "text-xl"
        : trimmed.length > 50
          ? "text-2xl"
          : "text-3xl";
  return (
    <div>
      <div className="text-xs opacity-70 mb-1">Прев'ю — як побачить ведучий</div>
      <div
        className={`pp-card-hero w-full text-center font-display font-bold leading-snug text-balance ${sizeClass}`}
      >
        <div
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-2 text-xs font-bold uppercase tracking-wider border"
          style={{
            color: CARD_TYPE_TINT[type],
            borderColor: `${CARD_TYPE_TINT[type]}66`,
            background: `${CARD_TYPE_TINT[type]}1a`,
          }}
        >
          <span aria-hidden>{CARD_TYPE_ICON[type]}</span>
        </div>
        <div>{trimmed}</div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs opacity-70 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
